// SPV Pool v1.0 — TanStack Query hooks for SPV pools, composition,
// eligibility criteria and IDUP. Follows the @/hooks/deals pattern:
// query-key factory objects + thin useQuery/useMutation wrappers; every
// mutation invalidates the relevant list / detail queries.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import { closingsApi, spvPoolsApi } from "@/api/spvPools";
import { certificationsApi } from "@/api/certifications";
import type {
  CashFlowPeriod,
  ClosingExecuteRequest,
  ClosingPreviewRequest,
  FinalizePoolRequest,
  IDUPStatus,
  PoolCompositionStatus,
  PoolEligibilityCriteriaUpdate,
  ReserveManualAdjustmentRequest,
  SpvPoolCreate,
  SpvPoolListQuery,
  SpvPoolUpdate,
  VfiPreviewRequest,
} from "@/types/spvPool";

export const spvPoolKeys = {
  all: ["spv-pools"] as const,
  lists: () => [...spvPoolKeys.all, "list"] as const,
  list: (query: SpvPoolListQuery | undefined) =>
    [...spvPoolKeys.lists(), query ?? {}] as QueryKey,
  detail: (id: string) => [...spvPoolKeys.all, "detail", id] as const,
  math: (id: string) => [...spvPoolKeys.all, "math", id] as const,
  composition: (id: string, status?: PoolCompositionStatus) =>
    [...spvPoolKeys.all, "composition", id, status ?? "all"] as const,
  eligibleSegments: (id: string) =>
    [...spvPoolKeys.all, "eligible-segments", id] as const,
  eligibilityCriteria: (id: string) =>
    [...spvPoolKeys.all, "eligibility-criteria", id] as const,
  idup: (id: string) => [...spvPoolKeys.all, "idup", id] as const,
  cashFlow: (
    id: string,
    period: CashFlowPeriod,
    start: string,
    end: string,
  ) =>
    [...spvPoolKeys.all, "cash-flow", id, period, start, end] as const,
  accountingStatus: (id: string) =>
    [...spvPoolKeys.all, "accounting-status", id] as const,
  stageGates: (id: string) =>
    [...spvPoolKeys.all, "stage-gates", id] as const,
  stageGateCheck: (id: string, targetStatus: string) =>
    [...spvPoolKeys.all, "stage-gate-check", id, targetStatus] as const,
  formation: (id: string) =>
    [...spvPoolKeys.all, "formation", id] as const,
  triggers: (id: string) =>
    [...spvPoolKeys.all, "triggers", id] as const,
  ssns: (id: string) =>
    [...spvPoolKeys.all, "ssns", id] as const,
  closings: (id: string) =>
    [...spvPoolKeys.all, "closings", id] as const,
  closingDetail: (paymentId: string) =>
    ["closings", "detail", paymentId] as const,
};

// --- Reads ----------------------------------------------------------------

export function useSpvPoolList(query?: SpvPoolListQuery, enabled = true) {
  return useQuery({
    queryKey: spvPoolKeys.list(query),
    queryFn: () => spvPoolsApi.list(query),
    enabled,
  });
}

export function useSpvPool(id: string | undefined) {
  return useQuery({
    queryKey: spvPoolKeys.detail(id ?? ""),
    queryFn: () => spvPoolsApi.get(id!),
    enabled: !!id,
  });
}

export function useSpvPoolMath(id: string | undefined) {
  return useQuery({
    queryKey: spvPoolKeys.math(id ?? ""),
    queryFn: () => spvPoolsApi.math(id!),
    enabled: !!id,
  });
}

export function usePoolComposition(
  id: string | undefined,
  status?: PoolCompositionStatus,
) {
  return useQuery({
    queryKey: spvPoolKeys.composition(id ?? "", status),
    queryFn: () => spvPoolsApi.composition(id!, status),
    enabled: !!id,
  });
}

export function usePoolEligibleSegments(id: string | undefined) {
  return useQuery({
    queryKey: spvPoolKeys.eligibleSegments(id ?? ""),
    queryFn: () => spvPoolsApi.eligibleSegments(id!),
    enabled: !!id,
  });
}

export function usePoolEligibilityCriteria(id: string | undefined) {
  return useQuery({
    queryKey: spvPoolKeys.eligibilityCriteria(id ?? ""),
    queryFn: () => spvPoolsApi.eligibilityCriteria(id!),
    enabled: !!id,
  });
}

export function usePoolIdup(id: string | undefined) {
  return useQuery({
    queryKey: spvPoolKeys.idup(id ?? ""),
    queryFn: () => spvPoolsApi.idup(id!),
    enabled: !!id,
  });
}

// SPV Pool v1.0 PR 7 — rendered IDUP content for the preview panel.
export function usePoolIdupPreview(
  poolId: string,
  idupId: string | undefined,
) {
  return useQuery({
    queryKey: [...spvPoolKeys.idup(poolId), "preview", idupId ?? ""] as const,
    queryFn: () => spvPoolsApi.idupPreview(poolId, idupId!),
    enabled: !!idupId,
  });
}

// --- Cash Flow (PR 6) -----------------------------------------------------

export function useGetPoolCashFlow(
  poolId: string | undefined,
  period: CashFlowPeriod,
  start: string,
  end: string,
) {
  return useQuery({
    queryKey: spvPoolKeys.cashFlow(poolId ?? "", period, start, end),
    queryFn: () =>
      spvPoolsApi.cashFlow.getCashFlow(poolId!, period, start, end),
    enabled: !!poolId,
  });
}

export function useGetAccountingStatus(poolId: string | undefined) {
  return useQuery({
    queryKey: spvPoolKeys.accountingStatus(poolId ?? ""),
    queryFn: () => spvPoolsApi.cashFlow.getAccountingStatus(poolId!),
    enabled: !!poolId,
  });
}

export function usePostReserveManualAdjustment(poolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReserveManualAdjustmentRequest) =>
      spvPoolsApi.cashFlow.postReserveManualAdjustment(poolId, body),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...spvPoolKeys.all, "cash-flow", poolId],
      });
    },
  });
}

// --- Stage-gate certifications (PR 8) -------------------------------------

// All seeded stage-gate definitions for the pool, grouped by transition.
export function useGetPoolStageGates(poolId: string | undefined) {
  return useQuery({
    queryKey: spvPoolKeys.stageGates(poolId ?? ""),
    queryFn: () => spvPoolsApi.stageGates(poolId!),
    enabled: !!poolId,
  });
}

// The gate checklist for advancing the pool to `targetStatus`.
export function useGetStageGateCheck(
  poolId: string | undefined,
  targetStatus: string | undefined,
) {
  return useQuery({
    queryKey: spvPoolKeys.stageGateCheck(poolId ?? "", targetStatus ?? ""),
    queryFn: () => spvPoolsApi.stageGateCheck(poolId!, targetStatus!),
    enabled: !!poolId && !!targetStatus,
  });
}

// Records a stage-gate certification on the pool via the generic
// Certifications module surface (entity type "SpvPool"). Per-definition
// role authorization is enforced server-side.
export function useCertifySpvPoolGate(poolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      definitionId,
      note,
    }: {
      definitionId: string;
      note?: string | null;
    }) =>
      certificationsApi.certify("SpvPool", poolId, {
        definitionId,
        note: note ?? null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: spvPoolKeys.stageGates(poolId) });
      qc.invalidateQueries({
        queryKey: [...spvPoolKeys.all, "stage-gate-check", poolId],
      });
    },
  });
}

// --- Mutations ------------------------------------------------------------

export function useCreateSpvPool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SpvPoolCreate) => spvPoolsApi.create(body),
    onSuccess: (pool) => {
      qc.invalidateQueries({ queryKey: spvPoolKeys.lists() });
      qc.setQueryData(spvPoolKeys.detail(pool.id), pool);
    },
  });
}

export function useUpdateSpvPool(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SpvPoolUpdate) => spvPoolsApi.update(id, body),
    onSuccess: (pool) => {
      qc.setQueryData(spvPoolKeys.detail(id), pool);
      qc.invalidateQueries({ queryKey: spvPoolKeys.lists() });
    },
  });
}

export function useAdvanceSpvPoolStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (target: string) => spvPoolsApi.advanceStatus(id, target),
    onSuccess: (pool) => {
      qc.setQueryData(spvPoolKeys.detail(id), pool);
      qc.invalidateQueries({ queryKey: spvPoolKeys.lists() });
      qc.invalidateQueries({ queryKey: spvPoolKeys.math(id) });
      qc.invalidateQueries({ queryKey: spvPoolKeys.stageGates(id) });
      qc.invalidateQueries({
        queryKey: [...spvPoolKeys.all, "stage-gate-check", id],
      });
    },
  });
}

function invalidateComposition(
  qc: ReturnType<typeof useQueryClient>,
  id: string,
) {
  qc.invalidateQueries({ queryKey: [...spvPoolKeys.all, "composition", id] });
  qc.invalidateQueries({ queryKey: spvPoolKeys.eligibleSegments(id) });
  qc.invalidateQueries({ queryKey: spvPoolKeys.math(id) });
}

export function useProposeSegments(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (segmentIds: string[]) => spvPoolsApi.propose(id, segmentIds),
    onSuccess: () => invalidateComposition(qc, id),
  });
}

export function useValidateComposition(id: string) {
  return useMutation({
    mutationFn: () => spvPoolsApi.validate(id),
  });
}

export function useCommitComposition(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (segmentIds: string[]) => spvPoolsApi.commit(id, segmentIds),
    onSuccess: () => invalidateComposition(qc, id),
  });
}

export function useContributeComposition(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      segmentIds,
      confirmPoolName,
    }: {
      segmentIds: string[];
      confirmPoolName: string;
    }) => spvPoolsApi.contribute(id, segmentIds, confirmPoolName),
    onSuccess: () => {
      invalidateComposition(qc, id);
      qc.invalidateQueries({ queryKey: spvPoolKeys.detail(id) });
    },
  });
}

export function useUpdateEligibilityCriteria(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PoolEligibilityCriteriaUpdate) =>
      spvPoolsApi.updateEligibilityCriteria(id, body),
    onSuccess: (criteria) => {
      qc.setQueryData(spvPoolKeys.eligibilityCriteria(id), criteria);
      qc.invalidateQueries({ queryKey: spvPoolKeys.eligibilityCriteria(id) });
      qc.invalidateQueries({ queryKey: spvPoolKeys.detail(id) });
    },
  });
}

export function useRegenerateIdup(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => spvPoolsApi.regenerateIdup(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: spvPoolKeys.idup(id) });
      qc.invalidateQueries({ queryKey: spvPoolKeys.detail(id) });
    },
  });
}

export function useUpdateIdupStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ idupId, status }: { idupId: string; status: IDUPStatus }) =>
      spvPoolsApi.updateIdupStatus(id, idupId, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: spvPoolKeys.idup(id) });
      qc.invalidateQueries({ queryKey: spvPoolKeys.detail(id) });
    },
  });
}

// --- Payment System v3.0 PR 6: pool formation -----------------------------

export function usePoolFormation(id: string | undefined) {
  return useQuery({
    queryKey: id ? spvPoolKeys.formation(id) : ["spv-pools", "formation", "void"],
    queryFn: () => spvPoolsApi.formation(id as string),
    enabled: !!id,
  });
}

export function usePoolTriggers(id: string | undefined) {
  return useQuery({
    queryKey: id ? spvPoolKeys.triggers(id) : ["spv-pools", "triggers", "void"],
    queryFn: () => spvPoolsApi.triggers(id as string),
    enabled: !!id,
  });
}

export function usePoolSsns(id: string | undefined) {
  return useQuery({
    queryKey: id ? spvPoolKeys.ssns(id) : ["spv-pools", "ssns", "void"],
    queryFn: () => spvPoolsApi.ssns(id as string),
    enabled: !!id,
  });
}

export function useFinalizePool(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FinalizePoolRequest) => spvPoolsApi.finalize(id, body),
    onSuccess: () => {
      // Finalize touches pool detail, math, composition, certifications,
      // formation history, triggers, and SSNs — invalidate them all so the
      // UI rehydrates with the freshly-fired S1 cascade visible.
      qc.invalidateQueries({ queryKey: spvPoolKeys.detail(id) });
      qc.invalidateQueries({ queryKey: spvPoolKeys.math(id) });
      qc.invalidateQueries({ queryKey: spvPoolKeys.composition(id) });
      qc.invalidateQueries({ queryKey: spvPoolKeys.stageGates(id) });
      qc.invalidateQueries({ queryKey: spvPoolKeys.formation(id) });
      qc.invalidateQueries({ queryKey: spvPoolKeys.triggers(id) });
      qc.invalidateQueries({ queryKey: spvPoolKeys.ssns(id) });
    },
  });
}

// --- Payment System v3.0 PR 7: closings + VFI ----------------------------

export function usePoolClosings(poolId: string | undefined) {
  return useQuery({
    queryKey: poolId
      ? spvPoolKeys.closings(poolId)
      : ["spv-pools", "closings", "void"],
    queryFn: () => spvPoolsApi.closings.list(poolId as string),
    enabled: !!poolId,
  });
}

export function useClosingDetail(paymentId: string | undefined) {
  return useQuery({
    queryKey: paymentId
      ? spvPoolKeys.closingDetail(paymentId)
      : ["closings", "detail", "void"],
    queryFn: () => closingsApi.get(paymentId as string),
    enabled: !!paymentId,
  });
}

export function usePreviewClosing(poolId: string) {
  return useMutation({
    mutationFn: (body: ClosingPreviewRequest) =>
      spvPoolsApi.closings.preview(poolId, body),
  });
}

export function useExecuteClosing(poolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ClosingExecuteRequest) =>
      spvPoolsApi.closings.execute(poolId, body),
    onSuccess: () => {
      // Closing fires the S3..S10 cascade and writes commitments, SSNs,
      // disbursements, segment-trigger events — invalidate every cached
      // surface that renders one of those.
      qc.invalidateQueries({ queryKey: spvPoolKeys.detail(poolId) });
      qc.invalidateQueries({ queryKey: spvPoolKeys.math(poolId) });
      qc.invalidateQueries({ queryKey: spvPoolKeys.closings(poolId) });
      qc.invalidateQueries({ queryKey: spvPoolKeys.triggers(poolId) });
      qc.invalidateQueries({ queryKey: spvPoolKeys.ssns(poolId) });
      qc.invalidateQueries({
        queryKey: [...spvPoolKeys.all, "cash-flow", poolId],
      });
      qc.invalidateQueries({ queryKey: ["investors", "commitments"] });
    },
  });
}

export function usePreviewVfi(poolId: string) {
  return useMutation({
    mutationFn: (body: VfiPreviewRequest) =>
      spvPoolsApi.vfi.preview(poolId, body),
  });
}

export function useRecordVfi(poolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VfiPreviewRequest) =>
      spvPoolsApi.vfi.record(poolId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: spvPoolKeys.detail(poolId) });
      qc.invalidateQueries({ queryKey: spvPoolKeys.math(poolId) });
      qc.invalidateQueries({
        queryKey: [...spvPoolKeys.all, "cash-flow", poolId],
      });
    },
  });
}
